/**
 * Plans sub-tab (v2 — DNKADM-0056 U1) — the Membership Plans list + Add dialog +
 * per-plan credit-deposit editor (handoff §5.9).
 *
 * **A plan IS the tier.** Loads `listMembershipPlans({ status })`
 * through `useResource`, keyed on the Active/Inactive/All filter (`useActiveFilter` +
 * `<ActiveFilter>`, §4.3). A table of name (the tier) → type (Sellable/Comp, from
 * `price > 0`) → who can buy (Members and staff / Staff only, from `staffOnly`,
 * DNKADM-0381) → term → price → status, each row with Edit, Copy (duplicate), a
 * who-can-buy flip (a whole-row PUT via `setMembershipPlanStaffOnly`), and a
 * status-gated lifecycle action: Publish (Draft), Deactivate (Active), or Reactivate
 * (Inactive) (§4.3, DNKAPI-0261). The shown Status chip is the lifecycle `status`
 * (Draft/Active/Inactive), not the `isActive` boolean; who-can-buy is orthogonal to it and
 * the Active/Inactive/All filter keeps meaning lifecycle only. The per-bucket **credit deposits**
 * are edited inside the {@link PlanDialog} (live on edit / staged-then-attached on create,
 * mirroring the Package dialog) — the old row-expand chevron editor is retired. "Add plan"
 * opens the dialog. Every write refetches + toasts. A first-run empty tenant (§8)
 * shows a strong empty state + CTA.
 *
 * A **Members** column (DNKADM-0407 U4) shows `impact.activeMemberships` (DNKAPI-0562) — a dash
 * when the server sends no `impact`.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a plan
 * name search and the Active/Inactive/All filter; every data column has a shared
 * {@link SortableHeader} (default Plan name ascending; unknown member counts sort last in
 * both directions). Search, filter and sort live in the URL (`plansListView.ts`).
 *
 * **Plan page and row (DNKADM-0496).** Editing a plan moved to its own page
 * ({@link PlanDetail}, read-only cards with one drawer each): the row, its name and its open
 * chevron go there, and the row's Edit is gone. "Add plan" still opens the {@link PlanDialog}.
 * A row is name, status (the service page's chip: Draft / Turned off / Active), price and
 * billing interval, and Members. Type, Who can buy and Term left the row; who can buy is on the
 * plan page, and the row keeps its flip. "What it includes" is not on the row: it needs each
 * plan's credit read, which the list does not make. The lifecycle actions read Turn on (publish
 * a Draft, reactivate a turned-off plan) and Turn off, the same calls as before.
 *
 * Money renders via the 0049 helpers (`formatMoney` with the tenant currency from
 * `useTenantCurrency`). No "GoHighLevel"/"GHL" reaches any rendered string.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { serverMessage } from '@/api/serverMessage';
import {
  copyMembershipPlan,
  deactivateMembershipPlan,
  listMembershipPlans,
  publishMembershipPlan,
  reactivateMembershipPlan,
  setMembershipPlanStaffOnly,
  type MembershipPlan,
} from '@/api/membershipPlans';
import { ServiceStatusChip } from '@/features/services/ServiceStatusChip';
import { PlanDialog } from './PlanDialog';
import {
  PLAN_DEFAULT_SORT,
  PLAN_SORT_KEYS,
  PLANS_URL_DEFAULTS,
  planMatches,
  planName,
  planPriceAndTermLabel,
  planSortValue,
  type PlanSortKey,
} from './plansListView';
import { ScreenError } from '@/components/ScreenError';

export function PlansTab() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  const navigate = useNavigate();
  // DNKADM-0430: search, status filter and sort come from the URL (`plansListView.ts`).
  const url = useListUrlState(PLANS_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, PLAN_SORT_KEYS, PLAN_DEFAULT_SORT);
  const { currency } = useTenantCurrency();

  const plans = useResource(
    useCallback(() => listMembershipPlans({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // The Add plan dialog (editing a plan is its page, DNKADM-0496).
  const [adding, setAdding] = useState(false);

  const refetch = plans.refetch;

  const handleDeactivate = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await deactivateMembershipPlan(plan.id);
        refetch();
        showToast('Plan turned off', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t turn the plan off.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await reactivateMembershipPlan(plan.id);
        refetch();
        showToast('Plan turned on', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t turn the plan on.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handlePublish = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await publishMembershipPlan(plan.id);
        refetch();
        showToast('Plan turned on', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t turn the plan on.', 'error');
      }
    },
    [refetch, showToast],
  );

  // DNKADM-0381 U1: flip who can buy from the row. A whole-row PUT (the same write as
  // Edit → Save with only `staffOnly` changed); the refetch shows the new column value.
  const handleStaffOnly = useCallback(
    async (plan: MembershipPlan, staffOnly: boolean) => {
      try {
        await setMembershipPlanStaffOnly(plan, staffOnly);
        refetch();
        showToast(
          staffOnly ? 'Plan is now staff only' : 'Plan is now open to members and staff',
          'success',
        );
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t change who can buy the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleCopy = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await copyMembershipPlan(plan.id);
        refetch();
        showToast('Plan duplicated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t duplicate the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  const rows = plans.data;
  const sortedRows = useMemo(
    () =>
      sortRows(
        (rows ?? []).filter((plan) => planMatches(plan, search)),
        (plan) => planSortValue(plan, sort.key),
        sort.dir,
        (plan) => plan.id,
      ),
    [rows, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: PlanSortKey) => setUrl(toSortParams(toggleSort(sort, key), PLAN_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const isFirstRun = useMemo(
    () => filter.value === 'active' && rows !== null && rows.length === 0,
    [filter.value, rows],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Each plan is a membership tier. Set its term, price, where it’s available, and the
          credits it deposits in each bucket on purchase or renewal.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setAdding(true)}
          sx={{ flexShrink: 0 }}
        >
          Add plan
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search plans',
          disabled: plans.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter plans by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {plans.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={260}
          role="status"
          aria-label="Loading membership plans"
          sx={{ borderRadius: 3 }}
        />
      )}

      {plans.status === 'error' && (
        <ScreenError
          error={plans.error}
          what="the membership plans"
          onRetry={plans.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {plans.status === 'ready' && rows && rows.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="card_membership" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {isFirstRun ? 'No membership plans yet' : 'No plans match this filter'}
          </Typography>
          {isFirstRun && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first plan — a tier with its term and price — then set the credits it
                deposits in each bucket.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setAdding(true)}
              >
                Add plan
              </Button>
            </>
          )}
        </Paper>
      )}

      {plans.status === 'ready' && rows && rows.length > 0 && (
        <TableContainer>
          <Table aria-label="Membership plans" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Plan" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <SortableHeader
                  label="Price"
                  col="price"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Members"
                  col="members"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <TableCell align="right">Actions</TableCell>
                <TableCell padding="checkbox" aria-label="Open" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((plan) => {
                const name = planName(plan);
                const openPlan = () => navigate(`/membership-credits/plans/${plan.id}`);
                return (
                  <TableRow key={plan.id} hover onClick={openPlan} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/membership-credits/plans/${plan.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {name}
                      </MuiLink>
                    </TableCell>
                    <TableCell>
                      <ServiceStatusChip status={plan.status} />
                    </TableCell>
                    <TableCell align="right">{planPriceAndTermLabel(plan, currency)}</TableCell>
                    <TableCell align="right">{plan.impact?.activeMemberships ?? '—'}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <IconAction
                        size="small"
                        label={`Duplicate plan ${name}`}
                        onClick={() => void handleCopy(plan)}
                      >
                        <Icon name="content_copy" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={
                          plan.staffOnly
                            ? `Open plan ${name} to members`
                            : `Make plan ${name} staff only`
                        }
                        onClick={() => void handleStaffOnly(plan, !plan.staffOnly)}
                      >
                        <Icon name={plan.staffOnly ? 'visibility' : 'visibility_off'} />
                      </IconAction>
                      {plan.status === 'DRAFT' && (
                        <IconAction
                          size="small"
                          label={`Turn on plan ${name}`}
                          onClick={() => void handlePublish(plan)}
                        >
                          <Icon name="publish" />
                        </IconAction>
                      )}
                      {plan.status === 'ACTIVE' && (
                        <IconAction
                          size="small"
                          label={`Turn off plan ${name}`}
                          onClick={() => void handleDeactivate(plan)}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      )}
                      {plan.status === 'INACTIVE' && (
                        <IconAction
                          size="small"
                          label={`Turn on plan ${name}`}
                          onClick={() => void handleReactivate(plan)}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ color: t.textGhost }}>
                      <IconAction
                        size="small"
                        label={`Open ${name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          openPlan();
                        }}
                      >
                        <Icon name="chevron_right" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {plans.status === 'ready' && rows && rows.length > 0 && sortedRows.length === 0 && (
        <ListNoMatch noun="plans" query={search} />
      )}

      <PlanDialog
        open={adding}
        plan={null}
        currency={currency}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
